/**
 * Service detail data transformer
 * Transforms service + addons data into rows for DataTable
 */
import type { ServiceDetailRow, TransformOptions } from './types'

/**
 * Transform service + addons data into rows with optional total row
 * Reusable across different service templates
 */
export const transformServiceDetailData = ({
    service,
    data,
    defaultTemplate = 'Server.VBM',
}: TransformOptions): ServiceDetailRow[] => {
    if (!data) return []

    const result: ServiceDetailRow[] = []
    const addons = data.addons || []
    const hasAddons = addons.length > 0

    // Main service row
    result.push({
        _rowType: 'main',
        _rowId: `main-${service?.service_id}`,
        service_id: service?.service_id,
        service_name: service?.service_name,
        name: service?.name,
        service_status: service?.service_status,
        service_template: service?.service_template || defaultTemplate,
        service_start_date: service?.service_start_date,
        service_stopped_date: service?.service_stopped_date,
        service_price_register: service?.service_price_register,
        service_price_recurring: service?.service_price_recurring,
        employee_names: service?.employee_names,
        current_startdate: service?.current_startdate,
        current_enddate: service?.current_enddate,
        continue_startdate: service?.continue_startdate,
        continue_enddate: service?.continue_enddate,
        configoptions: data.configoptions || [],
    })

    // Addon rows
    // NOTE: API returns addons with service_* fields (not addon_* prefix)
    addons.forEach((addon: any) => {
        result.push({
            _rowType: 'addon',
            _rowId: `addon-${addon.service_id}`,
            service_id: addon.service_id,
            service_name: addon.service_name,
            name: addon.name,
            service_status: addon.service_status,
            service_template: addon.service_template || defaultTemplate,
            service_start_date: addon.service_start_date,
            service_stopped_date: addon.service_stopped_date,
            service_price_register: addon.service_price_register,
            service_price_recurring: addon.service_price_recurring,
            employee_names: addon.employee_names || service?.employee_names,
            current_startdate:
                addon.current_startdate || service?.current_startdate,
            current_enddate: addon.current_enddate || service?.current_enddate,
            continue_startdate:
                addon.continue_startdate || service?.continue_startdate,
            continue_enddate:
                addon.continue_enddate || service?.continue_enddate,
            configoptions: addon.configoptions || [],
        })
    })

    // Total row (only if has addons)
    if (hasAddons) {
        // Combine all configoptions for total calculation
        const allConfigoptions = [
            ...(data.configoptions || []),
            ...addons.flatMap((addon: any) => addon.configoptions || []),
        ]

        // Sum prices (API uses service_* fields for addons)
        const totalPriceRegister =
            (service?.service_price_register || 0) +
            addons.reduce(
                (sum: number, addon: any) =>
                    sum + (addon.service_price_register || 0),
                0
            )
        const totalPriceRecurring =
            (service?.service_price_recurring || 0) +
            addons.reduce(
                (sum: number, addon: any) =>
                    sum + (addon.service_price_recurring || 0),
                0
            )

        result.push({
            _rowType: 'total',
            _rowId: `total-${service?.service_id}`,
            service_id: undefined,
            service_name: undefined,
            service_status: undefined,
            service_template: service?.service_template || defaultTemplate,
            service_price_register: totalPriceRegister,
            service_price_recurring: totalPriceRecurring,
            configoptions: allConfigoptions,
        })
    }

    return result
}
